/* ==========================================================================
   CP-HOME-MOTION: the homepage's GSAP layer (ScrollTrigger, SplitText, ScrollTo).
   Loaded on the homepage only, deferred, after the three GSAP files (layout/theme.liquid).

   Rules this file keeps, so motion never costs a sale:
   - Progressive enhancement. Liquid renders everything visible; hidden states are set here,
     just before each animation, so without JS (or if the CDN fails) the page is simply static.
   - Nothing on the first screen is hidden. The hero moves by transform only, so the H1 and the
     hero image paint at once and Largest Contentful Paint is untouched.
   - The size finder (second on the page) plays a timed intro on load instead of on scroll.
   - Every reveal plays once. Nothing scrubs against the reader except two quiet flourishes
     (the ordering line and the size sheet), and there is no scroll-jacking or smooth-scroller.
   - prefers-reduced-motion: none of this runs, except the instant jumps the page already had.
   - Interactions serve a decision: the drawing redraws when a use is chosen, the price answers
     the pack change, the FAQ opens smoothly, a mobile "Find your size" bar follows the reader
     until the finder is on screen.
   ========================================================================== */
(function () {
  "use strict";
  if (window.__cpHomeMotionRan) return;
  window.__cpHomeMotionRan = true;

  var g = window.gsap;
  if (!g || !window.ScrollTrigger) return;
  var ST = window.ScrollTrigger;
  var plugins = [ST];
  if (window.SplitText) plugins.push(window.SplitText);
  if (window.ScrollToPlugin) plugins.push(window.ScrollToPlugin);
  g.registerPlugin.apply(g, plugins);

  var EASE = "power3.out";
  var EASE_SOFT = "power2.out";
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function safe(fn) { try { fn(); } catch (e) { if (window.console) console.warn("[cp-home-motion]", e); } }
  function headerOffset() {
    var h = $(".cp-header");
    return h ? Math.round(h.getBoundingClientRect().height) + 12 : 80;
  }

  /* ---------------------------------------------------------- anchor scroll
     Runs even with reduced motion (it just jumps then), so the offset for the
     sticky header is always right. */
  function initAnchors() {
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
      var id = a.getAttribute("href").slice(1);
      var el = id && document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      if (reduce || !window.ScrollToPlugin) {
        window.scrollTo(0, el.getBoundingClientRect().top + window.scrollY - headerOffset());
      } else {
        g.to(window, { duration: 1.1, ease: "power3.inOut", scrollTo: { y: el, offsetY: headerOffset(), autoKill: true } });
      }
      if (history.replaceState) history.replaceState(null, "", "#" + id);
      /* keep keyboard and screen-reader users where the eye goes (skip links too) */
      if (!el.hasAttribute("tabindex") && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute("tabindex", "-1");
      try { el.focus({ preventScroll: true }); } catch (err) {}
    });
  }

  /* ------------------------------------------------------------ count up */
  function countUp(el, delay) {
    var raw = el.textContent.trim();
    var m = raw.match(/^([^\d]*)([\d][\d,]*(?:\.\d+)?)(.*)$/);
    if (!m) return;
    var pre = m[1], num = m[2], post = m[3];
    var target = parseFloat(num.replace(/,/g, ""));
    if (!isFinite(target) || target <= 0) return;
    var dec = (num.split(".")[1] || "").length;
    var grouped = num.indexOf(",") > -1;
    var o = { v: 0 };
    el.setAttribute("aria-label", raw);
    function paint() {
      var v = dec ? o.v.toFixed(dec) : Math.round(o.v);
      if (grouped && !dec) v = Number(v).toLocaleString("en-US");
      el.textContent = pre + v + post;
    }
    paint();
    g.to(o, {
      v: target, duration: 1.6, delay: delay || 0, ease: "power2.out", onUpdate: paint,
      onComplete: function () { el.textContent = raw; }
    });
  }
  function countOnView(els, trigger) {
    if (!els.length) return;
    ST.create({
      trigger: trigger || els[0], start: "top 88%", once: true,
      onEnter: function () { els.forEach(function (el, i) { countUp(el, i * 0.08); }); }
    });
  }

  /* -------------------------------------------------- headings, as a family
     Label, heading (line by line, masked), then the intro under it. */
  function revealHead(sec) {
    var label = $(".cp-label", sec);
    var h2 = $("h2", sec);
    if (!h2) return;
    var lede = h2.nextElementSibling && h2.nextElementSibling.tagName === "P" ? h2.nextElementSibling : null;
    if (!lede) {
      var head = h2.closest("header");
      lede = head ? $(":scope > p", head) : null;
    }
    var tl = g.timeline({ scrollTrigger: { trigger: h2, start: "top 86%", once: true } });
    if (label) tl.from(label, { autoAlpha: 0, x: -14, duration: 0.7, ease: EASE }, 0);
    if (window.SplitText) {
      var split = window.SplitText.create(h2, { type: "lines", mask: "lines", linesClass: "cp-line" });
      /* un-split once it has played, so the heading reflows naturally on resize */
      tl.from(split.lines, { yPercent: 105, duration: 1.05, ease: "power4.out", stagger: 0.09, onComplete: function () { split.revert(); } }, 0.08);
    } else {
      tl.from(h2, { autoAlpha: 0, y: 28, duration: 0.9, ease: EASE }, 0.08);
    }
    if (lede) tl.from(lede, { autoAlpha: 0, y: 18, duration: 0.9, ease: EASE_SOFT }, 0.3);
    return tl;
  }

  function rise(targets, trigger, opts) {
    targets = !targets ? [] : Array.isArray(targets) ? targets
      : (targets.length != null && !targets.nodeType) ? Array.prototype.slice.call(targets) : [targets];
    targets = targets.filter(Boolean);
    if (!targets.length) return;
    /* fromTo, not from: several of these carry Tailwind's `transition` class, and a from()
       re-read during ScrollTrigger.refresh() would catch them mid-CSS-transition and keep
       them hidden. Explicit end values can't be misread. */
    var o = Object.assign({ y: 36, x: 0, duration: 1, ease: EASE, stagger: 0.08 }, opts || {});
    var fromVars = { autoAlpha: 0, y: o.y, x: o.x };
    delete o.y; delete o.x;
    g.fromTo(targets, fromVars, Object.assign({
      autoAlpha: 1, y: 0, x: 0, clearProps: "opacity,visibility,transform",
      scrollTrigger: { trigger: trigger || targets[0], start: "top 86%", once: true }
    }, o));
  }

  /* ------------------------------------------------------------------ hero
     Transform only: the words and picture are already painted, they settle. */
  function initHero() {
    var hero = $("[data-cp-hero]");
    if (!hero) return;
    var h1 = $("[data-hero-h1]", hero);
    var media = $("[data-hero-media]", hero);
    var card = $("[data-hero-card]", hero);
    var tl = g.timeline({ defaults: { ease: EASE } });
    if (h1) tl.from(h1, { y: 26, duration: 1.1 }, 0);
    tl.from($$(".cp-label, h1 + p", hero), { y: 16, duration: 0.9, stagger: 0.06 }, 0.05);
    if (media) {
      var inner = media.firstElementChild;
      tl.from(media, { y: 20, duration: 1.2 }, 0);
      if (inner) tl.from(inner, { scale: 1.06, duration: 1.8, ease: "power2.out" }, 0);
    }
    if (card) tl.from(card, { y: 24, autoAlpha: 0, duration: 0.9 }, 0.55);

    /* the picture drifts a little slower than the page */
    if (media && media.firstElementChild) {
      g.to(media.firstElementChild, {
        yPercent: 6, ease: "none",
        scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: 0.6 }
      });
    }
    var stats = $("[data-hero-stats]", hero);
    if (stats) {
      rise($$(":scope > div", stats), stats, { y: 22, stagger: 0.07 });
      countOnView($$("[data-count]", stats), stats);
    }

    /* magnetic buttons (desktop pointers only) */
    if (fine) $$("a.min-h-\\[58px\\]", hero).forEach(magnet);
  }

  function magnet(el) {
    var xTo = g.quickTo(el, "x", { duration: 0.5, ease: "power3" });
    var yTo = g.quickTo(el, "y", { duration: 0.5, ease: "power3" });
    el.addEventListener("pointermove", function (e) {
      var r = el.getBoundingClientRect();
      xTo((e.clientX - (r.left + r.width / 2)) * 0.18);
      yTo((e.clientY - (r.top + r.height / 2)) * 0.3);
    });
    el.addEventListener("pointerleave", function () { xTo(0); yTo(0); });
  }

  /* ---------------------------------------------------------- size finder */
  function drawIn(panel) {
    var svg = panel && $("svg[role='img']", panel);
    if (!svg) return;
    var rects = $$("rect", svg);
    var bag = rects.filter(function (r) { return r.getAttribute("stroke") === "#1E1E1E"; })[0];
    var prod = rects.filter(function (r) { return r.getAttribute("fill") === "#E7713B"; })[0];
    var dims = $$("g[stroke='#1E1E1E'] line, text", svg);
    if (bag) {
      var len = 2 * (parseFloat(bag.getAttribute("width")) + parseFloat(bag.getAttribute("height")));
      g.fromTo(bag, { strokeDasharray: len, strokeDashoffset: len }, {
        strokeDashoffset: 0, duration: 1.1, ease: "power2.inOut",
        onComplete: function () { bag.style.strokeDasharray = ""; bag.style.strokeDashoffset = ""; }
      });
    }
    if (prod) {
      var drop = parseFloat(prod.getAttribute("height")) * 0.6;
      g.fromTo(prod, { y: -drop, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.8, ease: "back.out(1.4)", delay: 0.55 });
    }
    if (dims.length) g.fromTo(dims, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, stagger: 0.02, delay: 0.7 });
  }

  /* The finder sits right under the hero, so it doesn't wait for the scroll: it plays one
     timed sequence as the page opens, just after the hero settles. Label, heading line by
     line, intro, the use pills, then the product card with its bag drawing itself. */
  function initFinder() {
    var sec = $("#find-your-size");
    if (!sec) return;
    var tl = g.timeline({ delay: 0.35, defaults: { ease: EASE } });

    var label = $(".cp-label", sec);
    var h2 = $("h2", sec);
    var head = h2 && h2.closest("header");
    var lede = h2 && (h2.nextElementSibling && h2.nextElementSibling.tagName === "P" ? h2.nextElementSibling : (head ? $(":scope > p", head) : null));
    if (label) tl.fromTo(label, { autoAlpha: 0, x: -14 }, { autoAlpha: 1, x: 0, duration: 0.7, clearProps: "opacity,visibility,transform" }, 0);
    if (h2) {
      if (window.SplitText) {
        var split = window.SplitText.create(h2, { type: "lines", mask: "lines", linesClass: "cp-line" });
        tl.fromTo(split.lines, { yPercent: 105 }, { yPercent: 0, duration: 1, ease: "power4.out", stagger: 0.08, onComplete: function () { split.revert(); } }, 0.08);
      } else {
        tl.fromTo(h2, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.9, clearProps: "opacity,visibility,transform" }, 0.08);
      }
    }
    if (lede) tl.fromTo(lede, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.8, ease: EASE_SOFT, clearProps: "opacity,visibility,transform" }, 0.3);

    var tabs = $("[data-sf-tabs]", sec);
    if (tabs) tl.fromTo($$("[data-sf-tab]", tabs), { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.05, clearProps: "opacity,visibility,transform" }, 0.4);

    var first = $("[data-sf-panel]:not([hidden])", sec);
    if (first) {
      var card = first.firstElementChild;
      if (card) tl.fromTo(card, { autoAlpha: 0, y: 36 }, { autoAlpha: 1, y: 0, duration: 1, clearProps: "opacity,visibility,transform" }, 0.6);
      tl.call(drawIn, [first], 0.8);
      var rows = $$("[data-sf-form] label, [data-sf-form] .grid", first);
      if (rows.length) tl.fromTo(rows, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.05, ease: EASE_SOFT, clearProps: "opacity,visibility,transform" }, 0.85);
    }

    /* choosing a use: the new bag draws itself, the page re-measures */
    sec.addEventListener("click", function (e) {
      var t = e.target.closest("[data-sf-tab]");
      if (!t) return;
      requestAnimationFrame(function () {
        var p = document.getElementById(t.getAttribute("aria-controls"));
        if (p && !p.hidden) {
          drawIn(p);
          g.from($$("h3, [data-sf-form] label", p), { autoAlpha: 0, y: 10, duration: 0.5, stagger: 0.035, ease: EASE_SOFT, delay: 0.1 });
        }
        ST.refresh();
      });
    });

    /* choosing a pack: the price on the button answers */
    sec.addEventListener("change", function (e) {
      if (!e.target.matches("[data-sf-pack]")) return;
      var form = e.target.closest("[data-sf-form]");
      var price = form && $("[data-sf-add-price]", form);
      var btn = form && $("[data-sf-add]", form);
      if (price) g.fromTo(price, { yPercent: 60, autoAlpha: 0 }, { yPercent: 0, autoAlpha: 1, duration: 0.45, ease: EASE });
      if (btn) g.fromTo(btn, { scale: 0.985 }, { scale: 1, duration: 0.5, ease: "elastic.out(1, 0.5)" });
    });
  }

  /* ------------------------------------------------------- how ordering works
     On desktop an ink line travels the four steps as the reader scrolls,
     and each step's dot fills when the line reaches it. */
  function initOrdering() {
    var sec = $("#how-ordering-works");
    if (!sec) return;
    revealHead(sec);
    var ol = $("[data-ho-steps]", sec);
    if (!ol) return;
    var steps = $$("[data-ho-step]", ol);
    rise(steps.map(function (s) { return s.children; }).reduce(function (a, c) { return a.concat([].slice.call(c).filter(function (n) { return !n.hasAttribute("data-ho-node"); })); }, []), ol, { y: 24, stagger: 0.04, duration: 0.9 });

    var mm = g.matchMedia();
    mm.add("(min-width: 1024px)", function () {
      var line = document.createElement("span");
      line.setAttribute("aria-hidden", "true");
      line.style.cssText = "position:absolute;left:0;right:0;top:-1px;height:1.5px;background:#1E1E1E;transform-origin:0 50%;transform:scaleX(0);pointer-events:none";
      ol.appendChild(line);
      var nodes = $$("[data-ho-node]", ol);
      var keys = nodes.map(function (n) { return n.classList.contains("bg-brand-primary"); });
      g.set(nodes, { backgroundColor: "#FFFFFF", borderColor: "rgba(28,27,25,.3)", borderWidth: 1, borderStyle: "solid" });
      var tl = g.timeline({ scrollTrigger: { trigger: ol, start: "top 78%", end: "bottom 55%", scrub: 0.8 } });
      tl.to(line, { scaleX: 1, ease: "none", duration: 1 }, 0);
      nodes.forEach(function (n, i) {
        var at = i / nodes.length;
        var c = keys[i] ? "#E7713B" : "#1E1E1E";
        tl.to(n, { backgroundColor: c, borderColor: c, scale: 1.25, duration: 0.06, ease: "none" }, at)
          .to(n, { scale: 1, duration: 0.06 }, at + 0.06);
      });
      return function () { line.remove(); g.set(nodes, { clearProps: "all" }); };
    });
    /* below desktop the steps stack: each step's rule draws in and its dot fills as it arrives */
    mm.add("(max-width: 1023px)", function () {
      var made = [];
      $$("[data-ho-step]", ol).forEach(function (li) {
        var node = $("[data-ho-node]", li);
        var key = node && node.classList.contains("bg-brand-primary");
        var line = document.createElement("span");
        line.setAttribute("aria-hidden", "true");
        line.style.cssText = "position:absolute;left:0;right:0;top:-1px;height:1.5px;background:" + (key ? "#E7713B" : "#1E1E1E") + ";transform-origin:0 50%;transform:scaleX(0);pointer-events:none";
        li.appendChild(line);
        made.push(line);
        var tl = g.timeline({ scrollTrigger: { trigger: li, start: "top 85%", once: true } });
        tl.to(line, { scaleX: 1, duration: 0.9, ease: "power3.inOut" });
        if (node) tl.fromTo(node, { scale: 0.4 }, { scale: 1, duration: 0.5, ease: "back.out(2)" }, 0.5);
      });
      return function () { made.forEach(function (l) { l.remove(); }); };
    });
    var cta = ol.nextElementSibling;
    if (cta) rise(cta.children, cta, { y: 16, stagger: 0.06 });
  }

  /* ------------------------------------------------------------- reviews */
  function initReviews() {
    var sec = $("#reviews");
    if (!sec) return;
    revealHead(sec);
    countOnView($$("[data-count]", sec));
    var cards = $$("[data-cp-rv-card]", sec);
    rise(cards, cards[0], { y: 44, stagger: 0.1, duration: 1.1 });
    if (fine) cards.forEach(function (c) {
      c.addEventListener("pointerenter", function () { g.to(c, { y: -6, duration: 0.5, ease: EASE, boxShadow: "0 24px 40px -28px rgba(28,27,25,.35)" }); });
      c.addEventListener("pointerleave", function () { g.to(c, { y: 0, duration: 0.6, ease: EASE, boxShadow: "0 0 0 0 rgba(28,27,25,0)" }); });
    });
  }

  /* ---------------------------------------------------------- best sellers
     Each photo opens like a curtain from the bottom, then the words follow. */
  function initBestSellers() {
    var sec = $("#most-ordered");
    if (!sec) return;
    revealHead(sec);
    var cards = $$("[data-bs-card]", sec);
    if (!cards.length) return;
    var tl = g.timeline({ scrollTrigger: { trigger: cards[0], start: "top 86%", once: true } });
    cards.forEach(function (c, i) {
      var media = $("[data-bs-media]", c);
      var img = media && $("img", media);
      var words = $$("h3, p", c);
      if (media) tl.fromTo(media, { clipPath: "inset(100% 0% 0% 0% round 18px)" }, { clipPath: "inset(0% 0% 0% 0% round 18px)", duration: 1.15, ease: "power4.inOut" }, i * 0.1);
      if (img) tl.from(img, { scale: 1.12, duration: 1.5, ease: EASE }, i * 0.1);
      if (words.length) tl.from(words, { autoAlpha: 0, y: 12, duration: 0.7, stagger: 0.05, ease: EASE_SOFT }, i * 0.1 + 0.55);
    });
  }

  /* ------------------------------------------------------------ size sheet
     The printed sheet settles onto the ink card as it comes up the page. */
  function initSheet() {
    var sec = $("#size-sheet");
    if (!sec) return;
    revealHead(sec);
    var art = $("[data-sheet-art]", sec);
    var card = sec.firstElementChild;
    if (card) g.from(card, { autoAlpha: 0, y: 40, duration: 1.1, ease: EASE, scrollTrigger: { trigger: card, start: "top 88%", once: true } });
    if (art) {
      var paper = $("svg", art);
      if (paper) g.fromTo(paper, { rotate: -7, y: 50 }, {
        rotate: -1.5, y: -10, ease: "none",
        scrollTrigger: { trigger: sec, start: "top bottom", end: "bottom 40%", scrub: 0.8 }
      });
    }
    var list = $$("li", sec);
    rise(list, list[0], { y: 12, x: -8, stagger: 0.08, duration: 0.7 });
    var form = $("form", sec);
    if (form) rise(form, form, { y: 18, duration: 0.8 });
  }

  /* ------------------------------------------------------------------- faq
     Questions stack in; answers open and close with their real height. */
  function initFaq() {
    var sec = $("#faq");
    if (!sec) return;
    revealHead(sec);
    var items = $$("[data-faq-item]", sec);
    rise(items, items[0], { y: 18, stagger: 0.06, duration: 0.8 });
    var aside = $(".rounded-\\[18px\\]", sec);
    if (aside) rise(aside, aside, { y: 24 });

    items.forEach(function (d) {
      var sum = $("summary", d), body = $("[data-faq-body]", d);
      if (!sum || !body) return;
      sum.addEventListener("click", function (e) {
        e.preventDefault();
        if (d.__busy) return;
        d.__busy = true;
        if (d.open) {
          g.to(body, { height: 0, paddingBottom: 0, autoAlpha: 0, duration: 0.45, ease: "power2.inOut",
            onComplete: function () { d.open = false; g.set(body, { clearProps: "all" }); d.__busy = false; } });
        } else {
          d.open = true;
          g.from(body, { height: 0, paddingBottom: 0, autoAlpha: 0, duration: 0.55, ease: "power3.out",
            onComplete: function () { g.set(body, { clearProps: "all" }); d.__busy = false; ST.refresh(); } });
        }
      });
    });
  }


  /* ------------------------------------------------------ stacking cards
     Phones and desktop. A run of sections becomes a deck: each one sticks when it
     reaches the header (or, if it is taller than the screen, when its bottom
     reaches the bottom), and the next slides up over it with a rounded top
     edge while the one underneath settles back and dims. Sticky is plain CSS,
     so it is smooth and never pins the reader; GSAP only scrubs the depth.
     Everything after the deck is lifted above it. */
  function wrapOf(el) { return el && (el.closest(".shopify-section") || el); }
  function initStack() {
    var cards = [
      wrapOf($("#reviews")),
      wrapOf($("#most-ordered")),
      wrapOf($("#size-sheet"))
    ].filter(Boolean);
    if (cards.length < 2) return;
    var parent = cards[0].parentElement;
    if (cards.some(function (c) { return c.parentElement !== parent; })) return;

    var mm = g.matchMedia();
    mm.add({ phone: "(max-width: 767px)", wide: "(min-width: 768px)" }, function (ctx) {
      var radius = ctx.conditions.phone ? "20px 20px 0 0" : "28px 28px 0 0";
      var sibs = Array.prototype.slice.call(parent.children);
      var after = sibs.slice(sibs.indexOf(cards[cards.length - 1]) + 1);
      var shades = [], tweens = [];

      function place() {
        var hdr = headerOffset() - 12;
        cards.forEach(function (c) { c.style.top = Math.min(hdr, window.innerHeight - c.offsetHeight) + "px"; });
      }

      cards.forEach(function (c, i) {
        var inner = c.firstElementChild || c;
        c.style.position = "sticky";
        c.style.zIndex = String(2 + i);
        if (getComputedStyle(inner).backgroundColor === "rgba(0, 0, 0, 0)") inner.style.backgroundColor = "#FFFFFF";
        if (i > 0) {
          inner.style.borderRadius = radius;
          inner.style.boxShadow = "0 -30px 60px -40px rgba(28,27,25,.35)";
        }
        if (i < cards.length - 1) {
          var shade = document.createElement("div");
          shade.setAttribute("aria-hidden", "true");
          shade.style.cssText = "position:absolute;inset:0;background:#1E1E1E;opacity:0;pointer-events:none;z-index:5;border-radius:" + (i > 0 ? radius : "0");
          c.appendChild(shade);
          shades.push(shade);
          var next = cards[i + 1];
          tweens.push(g.timeline({
            scrollTrigger: { trigger: next, start: "top bottom", end: function () { return "top " + (parseFloat(next.style.top) || 0) + "px"; }, scrub: 0.4, invalidateOnRefresh: true }
          })
            .to(inner, { scale: ctx.conditions.phone ? 0.96 : 0.94, transformOrigin: "50% 0%", ease: "none" }, 0)
            .to(shade, { opacity: 0.16, ease: "none" }, 0));
        }
      });
      after.forEach(function (s) { s.style.position = "relative"; s.style.zIndex = String(2 + cards.length); });

      place();
      window.addEventListener("resize", place);
      ST.addEventListener("refreshInit", place);
      requestAnimationFrame(function () { ST.refresh(); });

      return function () {
        window.removeEventListener("resize", place);
        ST.removeEventListener("refreshInit", place);
        shades.forEach(function (sh) { sh.remove(); });
        cards.concat(after).forEach(function (c) { c.style.position = ""; c.style.zIndex = ""; c.style.top = ""; });
        cards.forEach(function (c) { var inner = c.firstElementChild || c; inner.style.borderRadius = ""; inner.style.boxShadow = ""; inner.style.backgroundColor = ""; g.set(inner, { clearProps: "transform" }); });
      };
    });
  }

  /* ---------------------------------------------- mobile "Find your size" bar
     Once the hero's button has scrolled away, a slim bar keeps the one action
     in reach. It steps aside while the finder itself (or the footer) is on screen. */
  function initStickyCta() {
    var finder = $("#find-your-size");
    if (!finder) return;
    var hero = $("[data-cp-hero]") || (finder.closest(".shopify-section") || finder).previousElementSibling;
    if (!hero) return;
    var src = $('a[href="#find-your-size"]', hero);
    var label = src ? src.textContent.trim() : "Find your size";
    var mm = g.matchMedia();
    mm.add("(max-width: 767px)", function () {
      var bar = document.createElement("a");
      bar.href = "#find-your-size";
      bar.className = "cp-home-cta";
      bar.innerHTML = '<span>' + label.replace(/</g, "&lt;") + '</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v15M6 13l6 6 6-6"/></svg>';
      bar.style.cssText = "position:fixed;left:16px;right:16px;bottom:calc(14px + env(safe-area-inset-bottom));z-index:34;display:flex;align-items:center;justify-content:center;gap:10px;min-height:54px;border-radius:999px;background:#1E1E1E;border:1px solid rgba(244,240,234,.18);color:#fff;font:500 15px/1 Poppins,sans-serif;letter-spacing:.01em;text-decoration:none;box-shadow:0 18px 40px -18px rgba(28,27,25,.6);visibility:hidden";
      $("svg", bar).style.cssText = "width:16px;height:16px;fill:none;stroke:currentColor;stroke-width:1.6";
      document.body.appendChild(bar);
      var show = g.to(bar, { autoAlpha: 1, y: 0, duration: 0.45, ease: EASE, paused: true });
      g.set(bar, { y: 24 });
      var afterHero = false, inFinder = false, atEnd = false;
      function sync() { if (afterHero && !inFinder && !atEnd) show.play(); else show.reverse(); }
      var a = ST.create({ trigger: hero, start: "bottom top+=80", onEnter: function () { afterHero = true; sync(); }, onLeaveBack: function () { afterHero = false; sync(); } });
      var b = ST.create({ trigger: finder, start: "top bottom", end: "bottom top", onToggle: function (s) { inFinder = s.isActive; sync(); } });
      var foot = $("footer") || $(".shopify-section-group-footer-group");
      var c = foot && ST.create({ trigger: foot, start: "top bottom", onToggle: function (s) { atEnd = s.isActive; sync(); } });
      return function () { a.kill(); b.kill(); if (c) c.kill(); bar.remove(); };
    });
  }

  function boot() {
    safe(initAnchors);
    if (reduce) return;
    document.documentElement.classList.add("cp-gsap");
    safe(initStack);
    safe(initHero);
    safe(initFinder);
    safe(initOrdering);
    safe(initReviews);
    safe(initBestSellers);
    safe(initSheet);
    safe(initFaq);
    safe(initStickyCta);
    /* fonts and lazy images change heights after first layout */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ST.refresh(); });
    window.addEventListener("load", function () { ST.refresh(); });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
